/**
 * Long polling tickets — pas de setInterval : une requête attend, puis on relance.
 * Utilise created_at / updated_at des tickets (aucune table supplémentaire).
 */
import AuthService from './auth.service'

const listeners = new Set()
let streamActive = false
let streamGeneration = 0
let streamSince = null
let abortController = null
let retryDelay = 2000

const MIN_RETRY_DELAY = 2000
const MAX_RETRY_DELAY = 60000

function getAuthToken () {
  try {
    return JSON.parse(localStorage.getItem('token') || 'null')
  } catch (error) {
    return null
  }
}

export function isIlabsUser () {
  return AuthService.checkRole('ROLE_ILABS')
}

function emitToListeners (payload) {
  listeners.forEach((listener) => {
    try {
      listener(payload)
    } catch (error) {
      console.error('[ticketStream]', error)
    }
  })
}

async function waitForTicketChanges () {
  const token = getAuthToken()
  if (!token) {
    throw new Error('no token')
  }

  abortController = new AbortController()
  const apiBase = process.env.API || '/api/'
  const params = new URLSearchParams({ timeout: '28' })
  if (streamSince) {
    params.set('since', streamSince)
  }
  const url = `${apiBase}auth/tickets/wait?${params.toString()}`

  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
    signal: abortController.signal,
  })

  if (!response.ok) {
    throw new Error(`wait HTTP ${response.status}`)
  }

  return response.json()
}

async function runStreamLoop (generation) {
  while (streamActive && streamGeneration === generation) {
    try {
      const data = await waitForTicketChanges()
      retryDelay = MIN_RETRY_DELAY

      if (!streamActive || streamGeneration !== generation) {
        break
      }

      if (data?.since) {
        streamSince = data.since
      }

      const ticketIds = Array.isArray(data?.ticketIds) ? data.ticketIds : []
      const action = data?.action || 'ping'

      if (ticketIds.length > 0 && action !== 'ping') {
        emitToListeners({
          eventName: 'ticket',
          ticketIds,
          action,
        })
      }
    } catch (error) {
      if (!streamActive || streamGeneration !== generation || error?.name === 'AbortError') {
        break
      }
      await new Promise((resolve) => setTimeout(resolve, retryDelay))
      retryDelay = Math.min(retryDelay * 2, MAX_RETRY_DELAY)
    } finally {
      abortController = null
    }
  }
}

export function subscribeTicketStream (listener) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function startTicketStream () {
  if (streamActive) {
    return
  }
  streamActive = true
  retryDelay = MIN_RETRY_DELAY
  streamGeneration += 1
  runStreamLoop(streamGeneration)
}

export function stopTicketStream () {
  streamActive = false
  streamGeneration += 1
  if (abortController) {
    abortController.abort()
    abortController = null
  }
}

/** Le long-polling ne doit pas tourner globalement : il est coûteux côté API. */
export function ensureAppTicketStream () {
  return false
}

/** @deprecated Utiliser ensureAppTicketStream */
export function ensureIlabsTicketStream () {
  ensureAppTicketStream()
}

/** Démarré uniquement sur la page tickets. */
export function ensureTicketStreamOnPage () {
  if (getAuthToken()) {
    startTicketStream()
  }
}

/** À la fermeture de la page tickets : on coupe toujours le long-polling. */
export function stopTicketStreamOnPage () {
  stopTicketStream()
}

export function isTicketStreamRunning () {
  return streamActive
}
